import React, { useState } from "react"

import { Tooltip } from "@coval/ui"
import { Select, SelectItem } from "@heroui/react"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  SELECT_TRIGGER_CLASSNAMES,
  TEXT_INPUT_STYLES,
} from "../styles"
import { normalizeLegacyPresetValue } from "../utils/presetVocabulary"

interface LoopDetectionConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

type LoopPresetKey = "strict" | "normal" | "lenient" | "custom"

const CUSTOM_OVERRIDE_KEYS = [
  "min_loop_duration_seconds",
  "min_repetition_count",
  "loop_acoustic_similarity_threshold",
] as const

const PRESET_OPTIONS: Array<{
  key: LoopPresetKey
  label: string
  description: string
}> = [
  {
    key: "strict",
    label: "Strict",
    description: "Catches more loops (0.6s, 2 reps)",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Balanced sensitivity (1.0s, 3 reps)",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags clearer loops (1.5s, 4 reps)",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const LoopDetectionConfig: React.FC<LoopDetectionConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const metadata = metricRecordData.metric_metadata
  const storedPreset = normalizeLegacyPresetValue(
    metadata?.loop_detection_preset,
  )
  const hasCustomOverrideAtMount = CUSTOM_OVERRIDE_KEYS.some(
    (key) => metadata?.[key] !== undefined && metadata?.[key] !== null,
  )
  const [currentPreset, setCurrentPreset] = useState<LoopPresetKey>(
    hasCustomOverrideAtMount
      ? "custom"
      : ((storedPreset as LoopPresetKey | undefined) ?? "normal"),
  )
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (keys: Iterable<React.Key> | "all") => {
    if (keys === "all") {
      return
    }
    const selectedKey = Array.from(keys)[0] as LoopPresetKey | undefined
    if (!selectedKey) {
      return
    }

    setCurrentPreset(selectedKey)

    const nextMetadata = {
      ...metadata,
      loop_detection_preset: selectedKey === "custom" ? undefined : selectedKey,
    }
    if (selectedKey !== "custom") {
      nextMetadata.min_loop_duration_seconds = undefined
      nextMetadata.min_repetition_count = undefined
      nextMetadata.loop_acoustic_similarity_threshold = undefined
    }

    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }

  const updateNumber = (
    key:
      | "min_loop_duration_seconds"
      | "min_repetition_count"
      | "loop_acoustic_similarity_threshold"
      | "display_severity_threshold",
    rawValue: string,
  ) => {
    const value = parseFloat(rawValue)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metadata,
        [key]: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Detection Sensitivity</span>
        </div>
        <Select
          aria-label="Loop Detection Sensitivity"
          selectedKeys={new Set([currentPreset])}
          onSelectionChange={handlePresetChange}
          classNames={SELECT_TRIGGER_CLASSNAMES}
        >
          {PRESET_OPTIONS.map((option) => (
            <SelectItem key={option.key} textValue={option.label}>
              <div className="flex flex-col">
                <span className="text-sm">{option.label}</span>
                <span className="text-xs text-coval-text-muted">
                  {option.description}
                </span>
              </div>
            </SelectItem>
          ))}
        </Select>
        <p className="text-xs text-coval-text-muted">
          Choose Custom to override individual thresholds.
        </p>
      </div>

      {isCustom && (
        <div className="grid grid-cols-2 gap-3">
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>Min Duration (s)</span>
              <Tooltip
                content="Shortest agent utterance the detector will consider as a loop candidate."
                side="top"
              >
                <Info className={INFO_ICON_STYLES} />
              </Tooltip>
            </div>
            <input
              type="number"
              step="0.1"
              min="0.1"
              className={TEXT_INPUT_STYLES}
              value={metadata?.min_loop_duration_seconds ?? ""}
              onChange={(e) =>
                updateNumber("min_loop_duration_seconds", e.target.value)
              }
              placeholder="1.0"
              aria-label="Minimum Loop Duration"
            />
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>Min Repetitions</span>
              <Tooltip
                content="A loop must repeat at least this many times to be flagged."
                side="top"
              >
                <Info className={INFO_ICON_STYLES} />
              </Tooltip>
            </div>
            <input
              type="number"
              step="1"
              min="2"
              className={TEXT_INPUT_STYLES}
              value={metadata?.min_repetition_count ?? ""}
              onChange={(e) =>
                updateNumber("min_repetition_count", e.target.value)
              }
              placeholder="3"
              aria-label="Minimum Repetitions"
            />
          </div>

          <div className={`${FIELD_CONTAINER_STYLES} col-span-2`}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>
                Acoustic Similarity Threshold (0–1)
              </span>
              <Tooltip
                content="DTW similarity above which two utterances cluster as the same loop. Higher = stricter. Default 0.50."
                side="top"
              >
                <Info className={INFO_ICON_STYLES} />
              </Tooltip>
            </div>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              className={TEXT_INPUT_STYLES}
              value={metadata?.loop_acoustic_similarity_threshold ?? ""}
              onChange={(e) =>
                updateNumber(
                  "loop_acoustic_similarity_threshold",
                  e.target.value,
                )
              }
              placeholder="0.50"
              aria-label="Acoustic Similarity Threshold"
            />
          </div>
        </div>
      )}

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={`${LABEL_ROW_STYLES} min-h-10`}>
          <span className={LABEL_STYLES}>Display Severity Threshold (0–1)</span>
          <Tooltip
            content="Hide loops with per-repetition severity below this value from the deep-dive list and the audio waveform highlights. Doesn't affect what the backend detects."
            side="top"
          >
            <Info className={INFO_ICON_STYLES} />
          </Tooltip>
        </div>
        <input
          type="number"
          step="0.05"
          min="0"
          max="1"
          className={TEXT_INPUT_STYLES}
          value={metadata?.display_severity_threshold ?? ""}
          onChange={(e) =>
            updateNumber("display_severity_threshold", e.target.value)
          }
          placeholder="0.4"
          aria-label="Display Severity Threshold"
        />
      </div>
    </div>
  )
}
